import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text12 from '@sdk/components/ui/typography/Text12';
import Row from '@sdk/components/ui/spacing/Row';
import Href from '@sdk/components/ui/Href';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { INTEGRATIONS_DETAILS } from '@sdk/integrations/constants';
import { IntegrationDetailType } from '@sdk/integrations/types';
import type { IntegrationFromApi } from '@sdk/integrations/types/apiTypes';
import type { IntegrationDetails } from '@sdk/integrations/types';

import { useNavigation } from '@web/navigation/NavigationProvider';
import { NavigateAction } from '@web/navigation/navigationProvider.types';

const DetailLine = styled.span`
  margin-bottom: 10px;
`;

type Props = {
  integration: IntegrationFromApi;
};

/**
 * This component renders details after unfolding integration.
 * So it gives an user a description of what's being synced and when.
 */
const IntegrationListItemContent: React.FC<React.PropsWithChildren<Props>> = ({ integration }) => {
  const details: IntegrationDetails[] = INTEGRATIONS_DETAILS[integration.type];
  const { navigate } = useNavigation();

  if (!details.length) {
    return null;
  }

  const syncDetails = details.filter(({ type }) => type === IntegrationDetailType.sync);
  const hasSyncDetails = Boolean(syncDetails.length);

  const exportDetails = details.filter(({ type }) => type === IntegrationDetailType.export);
  const hasExportDetails = Boolean(exportDetails.length);

  const textDetails = details.filter(({ type }) => type === IntegrationDetailType.text);
  const hasTextDetails = Boolean(textDetails.length);

  return (
    <>
      {hasSyncDetails && (
        <Row mBottom={10}>
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('This integration syncs the following information:')}
          </Text14>
        </Row>
      )}

      {hasSyncDetails &&
        syncDetails.map((detailSection, index) => (
          <DetailLine key={index}>
            {getTitle(detailSection, navigate)}&nbsp;
            <Text12 themeColor={ThemeColorType.gray}>{detailSection.description}</Text12>
          </DetailLine>
        ))}

      {hasExportDetails && (
        <Row mTop={20} mBottom={10}>
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('You can export the following information with this integration:')}
          </Text14>
        </Row>
      )}

      {hasExportDetails &&
        exportDetails.map((detailSection, index) => (
          <DetailLine key={index}>
            {getTitle(detailSection, navigate)}&nbsp;
            <Text12 themeColor={ThemeColorType.gray}>{detailSection.description}</Text12>
          </DetailLine>
        ))}

      {hasTextDetails &&
        textDetails.map((detailSection, index) => (
          <DetailLine key={index}>
            <Text12 themeColor={ThemeColorType.gray}>{detailSection.description}</Text12>
          </DetailLine>
        ))}
    </>
  );
};

/**
 * A little helper that extracts title (first word) of integration feature.
 * If details comes with a link it will genereate href to that part of the system (example: a href to Employees module)
 * otherwise it will just render a string.
 */
function getTitle({ title, sref }: IntegrationDetails, navigate: NavigateAction): JSX.Element {
  if (sref) {
    return (
      <Href onClick={() => navigate(sref)} fontSize={14} themeColor={ThemeColorType.primary} bold>
        {title}
      </Href>
    );
  }
  return (
    <Text14 themeColor={ThemeColorType.gray} bold>
      {title}
    </Text14>
  );
}

export default IntegrationListItemContent;
